import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState, type JSX, type ReactNode } from "react";
import { Provider } from "react-redux";

import { useAuth } from "../auth/AuthProvider.js";
import { api } from "../lib/api.js";
import WebSocketProvider from "./components/websocket-provider.js";
import { setAuthData } from "./lib/store/authSlice.js";
import { setPortalCurrency } from "./lib/utils.js";
import { store } from "./lib/store/index.js";

/**
 * Mounts the CRM ported from xop-edge-staff, and bridges this portal's
 * session into the Redux store that CRM reads its identity from.
 *
 * ─────────────────────────────────────────────────────────────────────
 * WHY A BRIDGE, and not a rewrite of the CRM's auth.
 * ─────────────────────────────────────────────────────────────────────
 * Every ported page reads `state.auth.user` / `.permissions` — the page
 * guards, `usePermissions`, the salesperson pickers, the "can I edit this"
 * checks. Rewriting those to read this app's `useAuth()` would mean editing
 * dozens of files in a copy that is meant to stay diffable against its
 * source. Seeding the store they already read is one file.
 *
 * ─────────────────────────────────────────────────────────────────────
 * THE PERMISSIONS BELOW GRANT NOTHING. This is the important part.
 * ─────────────────────────────────────────────────────────────────────
 * `usePermissions` decides what the UI RENDERS. It has never been what
 * decides what the server RETURNS — in the command center or here. Every
 * request still carries a reseller token minted by the BFF, and crm-api's
 * six-layer wall answers it: Layer 3 refuses any endpoint not on the
 * reseller allowlist, Layer 1 bounds every row to the actor's own subtree,
 * and money documents are additionally scoped to what the reseller issued.
 *
 * So this list is a statement about MENUS, not about authority. It names the
 * pages this portal routes to. Adding `admin:access` here would show a
 * reseller an Administration menu whose every entry 403s — which is why it
 * is deliberately absent.
 */
/**
 * The ported store's page grants, from the effective permission set (spec F
 * §6.3). `usePermissions` in the port reads `<page>:access` strings; this is
 * the one place that translates, so a section a person cannot `view` renders
 * the port's own access-denied guard instead of a table full of 403s.
 * `file_manager` is customer attachments, which follow customers:view in
 * crm-api; the port's guard for it stays open because the customer page it
 * hangs off is already gated.
 *
 * `quotes:access` was here until spec 2026-09-09 §3.2 removed the ported
 * quotes pages. Nothing in `src/crm` reads the key any more, and granting a
 * page that does not exist is how a dead menu entry survives a refactor.
 *
 * STILL GRANTS NOTHING — see the header. crm-api decides.
 */
export function pagePermissionsFor(effective: readonly string[]): string[] {
  const out: string[] = [];
  if (effective.includes("customers:view")) out.push("customers:access");
  if (effective.includes("contacts:view")) out.push("contacts:access");
  if (effective.includes("prospects:view")) out.push("prospects:access");
  // The port's usePermissions reads `<resource>:<action>` for non-page checks,
  // so the key passes through under its own name. This grants nothing —
  // crm-api's reseller_permission "prospects:edit" is the wall; this only
  // decides whether the button is drawn.
  if (effective.includes("prospects:edit")) out.push("prospects:edit");
  if (effective.includes("invoices:view")) out.push("invoices:access");
  out.push("file_manager:access");
  return out;
}

interface CrmProfileResponse {
  user?: {
    id?: number;
    email?: string;
    first_name?: string | null;
    last_name?: string | null;
    full_name?: string | null;
  };
  reseller?: { id: number; name: string; currency?: string | null };
}

export function CrmRoot({ children }: { children: ReactNode }): JSX.Element {
  const { user, permissions } = useAuth();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    void (async () => {
      // The CRM's own identity, from crm-api — not from this app's session.
      // It carries the PERSISTED user id that every ownership check in the
      // ported code compares against (`owner_id === user.id`,
      // "assigned to me", the inline editors' disabled states). Using the
      // portal's session id instead would silently break all of them.
      let profile: CrmProfileResponse | null = null;
      try {
        profile = await api<CrmProfileResponse>("/api/crm/v1/profile");
        // The reseller's trading currency, so the CRM's tables and totals
        // stop saying "$" to a euro reseller. `crm/lib/utils.ts` explains why
        // this is session-level rather than threaded through every call site,
        // and which edge it leaves.
        setPortalCurrency(profile?.reseller?.currency);
      } catch {
        // Fall through: the store is seeded from the portal session below,
        // which is enough to render. A CRM call that needs the real id will
        // fail on its own terms rather than being hidden behind a spinner.
      }
      if (cancelled) return;

      store.dispatch(
        setAuthData({
          user: {
            id: profile?.user?.id ?? 0,
            email: profile?.user?.email ?? user.email,
            first_name: profile?.user?.first_name ?? user.name.split(" ")[0] ?? "",
            last_name: profile?.user?.last_name ?? user.name.split(" ").slice(1).join(" "),
            full_name: profile?.user?.full_name ?? user.name,
            // NO ROLES, deliberately. `usePermissions` and the page guard
            // both treat an `admin` role as "can access every page" — handing
            // a reseller one would open menus whose endpoints all refuse it.
            roles: [],
            is_super_admin: false,
            // The page guard refuses an inactive or unverified account before
            // it looks at permissions at all. Both are true by construction
            // here: crm-api resolved an ACTIVE ResellerMembership for this
            // email or the session would not exist (auth gate 3), and
            // portal-api verified the address before the account could sign
            // in. Stating them is not a claim this file is making up — it is
            // relaying what the two upstreams already established.
            active: true,
            email_verified: true,
          } as never,
          tenant: {
            id: user.resellerId,
            name: user.resellerName,
            slug: String(user.resellerId),
          } as never,
          permissions: pagePermissionsFor(permissions),
        }),
      );
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, permissions]);

  if (!ready) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 10 }}>
        <CircularProgress size={26} aria-label="Loading" />
      </Box>
    );
  }

  // `WebSocketProvider` is required, not optional: several ported components
  // call `useWebSocketContext()`, which throws outside it. The service it
  // wraps is INERT here (see lib/websocket.ts — crm-api refuses a
  // reseller-portal token on /cable, deliberately), so this provides the
  // context those components need without opening a connection that would
  // only be refused.
  return (
    <Provider store={store}>
      <WebSocketProvider>{children}</WebSocketProvider>
    </Provider>
  );
}
